/**
 * RecentActivityPanel — Executive Home Recent Activity feed
 * (Patch 36.0 · Project Obsidian).
 *
 * Deterministic renderer of the backend activity feed. The frontend
 * NEVER composes, ranks, or interprets activity — every row comes from
 * ``GET /workspaces/{workspace_id}/activity`` verbatim.
 *
 * Metadata is collapsed by default (progressive disclosure) and reveals
 * on demand. Workspace switching invalidates the query so no stale
 * items from a previous workspace ever bleed through.
 */
import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Activity, ChevronDown, ChevronRight, Plus, RefreshCw,
} from "lucide-react";

import type { ActivityItem, ActivityType } from "@/entities/activity/api";
import {
  flattenActivityPages,
  useInfiniteWorkspaceActivity,
} from "@/entities/activity/api";
import { useWorkspaceStore } from "@/app/store/workspace-store";
import { Button } from "@/shared/ui/button";
import { Card } from "@/shared/ui/card";
import { Skeleton } from "@/shared/ui/skeleton";

const TYPE_TONE: Record<ActivityType, string> = {
  business_brain_approved: "text-emerald-400",
  campaign_created: "text-[var(--md-ivory-0)]",
  campaign_completed: "text-emerald-400",
  asset_rendered: "text-[var(--md-ivory-0)]",
  asset_approved: "text-emerald-400",
  asset_vaulted: "text-emerald-400",
  publication_scheduled: "text-[var(--md-ivory-0)]",
  publication_published: "text-emerald-400",
  publication_failed: "text-red-400",
  publication_recovered: "text-amber-400",
  akinex_recommendation_accepted: "text-emerald-400",
  akinex_recommendation_dismissed: "text-[var(--md-fog)]",
};

// Type-aware accessible label — severity is NEVER color-only.
const TYPE_ROLE_LABEL: Record<ActivityType, string> = {
  business_brain_approved: "Milestone",
  campaign_created: "Activity",
  campaign_completed: "Milestone",
  asset_rendered: "Activity",
  asset_approved: "Milestone",
  asset_vaulted: "Milestone",
  publication_scheduled: "Activity",
  publication_published: "Milestone",
  publication_failed: "Alert",
  publication_recovered: "Recovery",
  akinex_recommendation_accepted: "Decision",
  akinex_recommendation_dismissed: "Decision",
};

export interface RecentActivityPanelProps {
  limit?: number;
}

export function RecentActivityPanel({
  limit = 8,
}: RecentActivityPanelProps): JSX.Element {
  const workspaceId = useWorkspaceStore((s) => s.currentWorkspaceId);
  const q = useInfiniteWorkspaceActivity(workspaceId, { limit });

  const items = flattenActivityPages(q.data);
  const count = items.length;

  const state: "loading" | "error" | "empty" | "populated" | "no-workspace" =
    !workspaceId
      ? "no-workspace"
      : q.isLoading || q.isPending
        ? "loading"
        : q.isError
          ? "error"
          : count === 0
            ? "empty"
            : "populated";

  const canLoadMore = q.hasNextPage && !q.isFetchingNextPage;

  return (
    <Card
      data-testid="obsidian-recent-activity"
      data-state={state}
      data-count={count}
      className="p-5 border-white/10 obsidian-card"
    >
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4 text-[var(--md-fog)]" aria-hidden />
          <h3 className="text-sm font-semibold text-[var(--md-ivory-0)]">
            Recent Activity
          </h3>
        </div>
        <Button
          size="sm"
          variant="ghost"
          onClick={() => q.refetch()}
          disabled={q.isFetching}
          data-testid="obsidian-activity-refresh"
          aria-label="Refresh activity feed"
        >
          <RefreshCw
            className={`h-3.5 w-3.5 ${q.isFetching ? "animate-spin" : ""}`}
            aria-hidden
          />
        </Button>
      </div>

      {state === "loading" ? (
        <div
          data-testid="obsidian-activity-loading"
          className="space-y-2"
          aria-busy="true"
        >
          <Skeleton className="h-6 w-full" />
          <Skeleton className="h-6 w-4/5" />
          <Skeleton className="h-6 w-3/4" />
        </div>
      ) : state === "error" ? (
        <p
          data-testid="obsidian-activity-error"
          className="text-sm text-[var(--md-fog)]"
        >
          Activity is temporarily unavailable. It will refresh automatically.
        </p>
      ) : state === "empty" ? (
        <EmptyActivity />
      ) : (
        <>
          <ul
            data-testid="obsidian-activity-list"
            data-count={count}
            className="space-y-1"
          >
            {items.map((item) => (
              <ActivityRow key={item.id} item={item} />
            ))}
          </ul>
          {q.hasNextPage ? (
            <div className="mt-3 flex justify-center">
              <Button
                size="sm"
                variant="ghost"
                disabled={!canLoadMore}
                onClick={() => q.fetchNextPage()}
                data-testid="obsidian-activity-load-more"
                className="text-[12px] text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
              >
                {q.isFetchingNextPage ? (
                  <RefreshCw
                    className="h-3.5 w-3.5 mr-1.5 animate-spin"
                    aria-hidden
                  />
                ) : (
                  <Plus className="h-3.5 w-3.5 mr-1.5" aria-hidden />
                )}
                {q.isFetchingNextPage ? "Loading..." : "Load more"}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </Card>
  );
}

function EmptyActivity(): JSX.Element {
  return (
    <div
      data-testid="obsidian-activity-empty"
      className="rounded-lg border border-white/5 bg-black/10 p-4"
    >
      <p className="text-sm font-medium text-[var(--md-ivory-0)]">
        No recent activity yet.
      </p>
      <p className="mt-1 text-xs text-[var(--md-fog)]">
        As work moves through Orkexra, completed actions and important
        changes will appear here.
      </p>
    </div>
  );
}

function ActivityRow({ item }: { item: ActivityItem }): JSX.Element {
  const [open, setOpen] = useState(false);
  const testIdRoot = `obsidian-activity-item-${item.id}`;
  const tone = TYPE_TONE[item.type] ?? "text-[var(--md-ivory-0)]";
  const role = TYPE_ROLE_LABEL[item.type] ?? "Activity";
  const hasMetadata = Object.keys(item.metadata ?? {}).length > 0;
  // Suppress dead-end self-referential links: an Akinex decision row
  // whose action navigates to /home would land the founder on the page
  // they're already on. Hide the action link entirely for that case.
  const showAction = !!item.action && !isSelfReferentialAction(item);

  return (
    <li
      data-testid={testIdRoot}
      data-type={item.type}
      className="group rounded-lg border border-transparent px-2 py-2 hover:border-white/5 hover:bg-white/[0.02] transition"
    >
      <div className="flex items-start gap-3">
        <span
          aria-label={role}
          data-testid={`${testIdRoot}-role`}
          className={`mt-1 inline-block h-1.5 w-1.5 rounded-full ${
            tone.replace("text-", "bg-")
          }`}
        />
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-baseline gap-x-2">
            <span
              data-testid={`${testIdRoot}-title`}
              className={`text-sm font-medium ${tone}`}
            >
              {item.title}
            </span>
            <time
              dateTime={item.occurred_at}
              data-testid={`${testIdRoot}-time`}
              className="text-[11px] text-[var(--md-fog)]"
            >
              {formatRelative(item.occurred_at)}
            </time>
          </div>
          <p
            data-testid={`${testIdRoot}-description`}
            className="text-xs text-[var(--md-fog)]"
          >
            {item.description}
          </p>
          {hasMetadata ? (
            <div className="mt-1">
              <button
                type="button"
                onClick={() => setOpen((v) => !v)}
                aria-expanded={open}
                data-testid={`${testIdRoot}-details-toggle`}
                className="inline-flex items-center gap-1 text-[11px] text-[var(--md-fog)] hover:text-[var(--md-ivory-0)]"
              >
                {open ? (
                  <ChevronDown className="h-3 w-3" aria-hidden />
                ) : (
                  <ChevronRight className="h-3 w-3" aria-hidden />
                )}
                Details
              </button>
              {open ? (
                <dl
                  data-testid={`${testIdRoot}-details`}
                  className="mt-1 grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11px] text-[var(--md-fog)]"
                >
                  {Object.entries(item.metadata ?? {}).map(([k, v]) => (
                    <div key={k} className="contents">
                      <dt className="uppercase tracking-wider">{k}</dt>
                      <dd className="font-mono text-[var(--md-ivory-0)]">
                        {formatMetaValue(v)}
                      </dd>
                    </div>
                  ))}
                </dl>
              ) : null}
            </div>
          ) : null}
        </div>
        {showAction && item.action ? (
          <Link
            to={item.action.path}
            data-testid={`${testIdRoot}-action`}
            className="text-[11px] text-[var(--md-fog)] hover:text-[var(--md-ivory-0)] whitespace-nowrap"
          >
            {item.action.label} →
          </Link>
        ) : null}
      </div>
    </li>
  );
}

function isSelfReferentialAction(item: ActivityItem): boolean {
  const path = item.action?.path;
  if (!path) return false;
  // Recommendation timeline rows currently route to /home — the same
  // page the founder is already viewing. Hide the CTA.
  if (
    item.type === "akinex_recommendation_accepted" ||
    item.type === "akinex_recommendation_dismissed"
  ) {
    return path === "/home" || path === "/";
  }
  return false;
}

function formatMetaValue(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string" || typeof v === "number" || typeof v === "boolean") {
    return String(v);
  }
  try {
    return JSON.stringify(v);
  } catch {
    return "—";
  }
}

function formatRelative(iso: string): string {
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return "";
  const diff = Date.now() - then;
  const s = Math.max(1, Math.floor(diff / 1000));
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
}
